import { ChangeDetectionStrategy, Component, computed, input, output } from '@angular/core';
import type { NotificationItem } from './notifications.models';
import { NOTIFICATION_PRODUCT_FILTER_LABELS } from './notifications.models';
import { relativeTime } from './relative-time';
import { linkifyBody } from './linkify-body';

/** A finished retouch run: body = its project name, link = its photos — the bell shows the one and opens the other. */
export const isRunDone = (item: Pick<NotificationItem, 'eventType'>): boolean => item.eventType === 'retouch.run_done';

// One feed/dropdown row: unread dot, title, body, product tag + relative time.
// Pure UI — the click meaning (open link, mark read) belongs to the parent.
//
// The whole row takes the mouse, but the accessible control is the title BUTTON: the body carries
// real anchors, and ARIA forbids a link inside anything that is a button. The button's own click
// bubbles to the row, so keyboard activation costs no handler of its own; an anchor stops its click
// so opening a link never also fires the row.
@Component({
  selector: 'app-notification-row',
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div
      class="flex w-full cursor-pointer items-start gap-3 rounded-[var(--r-s)] px-3 py-[10px] text-left hover:bg-panel3"
      (click)="activate.emit()"
    >
      <span
        class="mt-[7px] size-2 shrink-0 rounded-full"
        [class.bg-acc]="!item().isRead"
        [class.bg-line2]="item().isRead"
        [attr.aria-label]="item().isRead ? 'Прочитано' : 'Не прочитано'"
      ></span>
      <span class="min-w-0 flex-1">
        <button
          type="button"
          class="block w-full truncate text-left text-sm font-semibold"
          [class.text-ink]="!item().isRead"
          [class.text-c2]="item().isRead"
        >
          {{ item().title }}
        </button>
        @if (!compact()) {
          <span
            data-testid="notification-body"
            class="mt-0.5 line-clamp-2 text-sm leading-relaxed break-words whitespace-pre-line text-c3"
          >
            @for (segment of segments(); track segment) {
              @if (segment.href) {
                <a
                  [href]="segment.href"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="text-acc underline underline-offset-2 hover:brightness-110"
                  (click)="$event.stopPropagation()"
                  >{{ segment.text }}</a
                >
              } @else {
                {{ segment.text }}
              }
            }
          </span>
        } @else if (subline(); as text) {
          <span data-testid="notification-subline" class="mt-0.5 block truncate text-xs text-c2">{{ text }}</span>
        }
        <span class="mt-1 flex items-center gap-2 text-xs text-c3">
          @if (productLabel(); as label) {
            <span class="rounded-full bg-panel3 px-2 py-px">{{ label }}</span>
          }
          <span>{{ time() }}</span>
        </span>
      </span>
    </div>
  `,
})
export class NotificationRowComponent {
  readonly item = input.required<NotificationItem>();
  // Compact = dropdown mode: title + meta only; a run's project name rides one line under the title.
  readonly compact = input(false);
  readonly activate = output<void>();

  protected readonly time = computed(() => relativeTime(this.item().createdAt));

  protected readonly subline = computed(() => (isRunDone(this.item()) ? this.item().body : ''));

  protected readonly segments = computed(() => linkifyBody(this.item().body ?? ''));

  protected readonly productLabel = computed(() => {
    const product = this.item().product;
    return product ? (NOTIFICATION_PRODUCT_FILTER_LABELS[product] ?? product) : null;
  });
}
